/**
 * Command palette (Cmd+K). Fuzzy-search across all builder actions.
 * Uses the combobox+listbox ARIA pattern: focus stays in the input,
 * active item tracked via aria-activedescendant.
 */

import {
  SAVE_LABELS,
  PROJECT_LABELS,
  PUBLISH_LABELS,
  HELP_LABELS
} from '../shared/ui-vocabulary.js';
import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { getSelectedInstanceId, hasSectionClipboard } from './shell.js';
import { createModal } from './components.js';
import { getSharedType } from '../sections/section-manager.js';
import { stepCommentSelection } from './comment-sidebar.js';

// Copy/Duplicate apply only to page sections — shared sections (nav/footer)
// live outside page arrays and can't be re-pasted/cloned. Disable rather than
// letting the action fire and fail after the click (punish-then-explain).
function isPageSectionSelected() {
  const id = getSelectedInstanceId();
  return !!id && !getSharedType(id);
}

function hasActivePage() {
  const state = store.getState();
  return !!state?.pages?.some?.((page) => page.id === state.activePage);
}

function canDeleteActivePage() {
  const state = store.getState();
  return (
    Array.isArray(state?.pages) &&
    state.pages.length > 1 &&
    state.pages.some((page) => page.id === state.activePage)
  );
}

// ── Action registry ──
//
// Each action shape: { id, label, shortcut, section, keywords, handler,
//                      isEnabled?, type?, meta? }
//   type — one of 'action' | 'section' | 'template' | 'page' (default 'action').
//          Bounded sets ('action', 'page') always show on empty query;
//          unbounded sets ('section', 'template') only render once the user
//          starts typing, so a 4,000+ section registry doesn't choke the UI.
//   meta — arbitrary payload forwarded to the handler.

const _actions = [];

// Cap the visible result list for non-empty queries so a broad fuzzy match
// (e.g. "h") doesn't render thousands of rows. The footer shows N of M.
const MAX_RESULTS = 50;
const ACTIONS_CHANGED_EVENT = 'command-palette:actions-changed';

/**
 * Precompute the lowercased search haystack ONCE per action, at register/replace
 * time, instead of rebuilding `label + keywords` for every action on every
 * keystroke (the section set alone is ~4.6k entries). Substring semantics are
 * unchanged — `matchActions` still does `words.every(w => haystack.includes(w))`.
 * @param {object} action
 * @returns {object} the same action, with `_haystack` attached.
 */
function stampHaystack(action) {
  action._haystack = [
    (action.label || '').toLowerCase(),
    ...(action.keywords || []).map((k) => String(k).toLowerCase())
  ].join(' ');
  return action;
}

export function registerAction(action) {
  _actions.push(stampHaystack({ type: 'action', ...action }));
  eventBus.emit(ACTIONS_CHANGED_EVENT, { type: 'action' });
}

/**
 * Remove every action where `predicate(action) === true`. Used by bulk-managed
 * sets like the dynamic page list — `unregisterActions(a => a.type === 'page')`.
 */
export function unregisterActions(predicate) {
  for (let i = _actions.length - 1; i >= 0; i--) {
    if (predicate(_actions[i])) _actions.splice(i, 1);
  }
}

/**
 * Swap the full set of actions for a given `type`. Used during boot to
 * register sections/templates after their indexes load, and on every
 * `state-changed` to refresh the page list. Each entry is tagged with the
 * given `type` even if the caller didn't set one — keeps callers honest.
 */
export function replaceActionsForType(type, actions) {
  unregisterActions((a) => a.type === type);
  for (const a of actions) _actions.push(stampHaystack({ ...a, type }));
  eventBus.emit(ACTIONS_CHANGED_EVENT, { type });
}

const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
const mod = isMac ? 'Cmd' : 'Ctrl';

// Built-in actions (registered on first open)
let _builtinsRegistered = false;

function ensureBuiltins() {
  if (_builtinsRegistered) return;
  _builtinsRegistered = true;

  const mdash = '\u2014';
  const actions = [
    {
      id: 'add-section',
      label: 'Add Section',
      shortcut: mdash,
      section: 'Section',
      keywords: ['insert', 'new'],
      handler: () => eventBus.emit('palette:open-add-section')
    },
    {
      id: 'duplicate-section',
      label: 'Duplicate Section',
      shortcut: `${mod}+D`,
      section: 'Section',
      keywords: ['copy', 'clone'],
      handler: () => eventBus.emit('cmd:duplicate-section'),
      isEnabled: isPageSectionSelected
    },
    {
      id: 'copy-section',
      label: 'Copy Section',
      shortcut: `${mod}+C`,
      section: 'Section',
      keywords: ['clipboard'],
      handler: () => eventBus.emit('cmd:copy-section'),
      isEnabled: isPageSectionSelected
    },
    {
      id: 'paste-section',
      label: 'Paste Section',
      shortcut: `${mod}+V`,
      section: 'Section',
      keywords: ['clipboard', 'insert'],
      handler: () => eventBus.emit('cmd:paste-section'),
      isEnabled: () => hasSectionClipboard()
    },
    {
      id: 'delete-section',
      label: 'Delete Section',
      shortcut: 'Del',
      section: 'Section',
      keywords: ['remove'],
      handler: () => eventBus.emit('cmd:delete-section'),
      isEnabled: () => !!getSelectedInstanceId()
    },
    {
      id: 'move-section-up',
      label: 'Move Section Up',
      shortcut: `${mod}+Shift+↑`,
      section: 'Section',
      keywords: ['reorder', 'raise'],
      handler: () => eventBus.emit('cmd:move-section-up'),
      isEnabled: isPageSectionSelected
    },
    {
      id: 'move-section-down',
      label: 'Move Section Down',
      shortcut: `${mod}+Shift+↓`,
      section: 'Section',
      keywords: ['reorder', 'lower'],
      handler: () => eventBus.emit('cmd:move-section-down'),
      isEnabled: isPageSectionSelected
    },
    {
      id: 'rename-section',
      label: 'Rename Section',
      shortcut: 'F2',
      section: 'Section',
      keywords: ['label', 'name'],
      handler: () => eventBus.emit('cmd:rename-section'),
      isEnabled: isPageSectionSelected
    },
    {
      id: 'add-page',
      label: 'Add Page',
      shortcut: `${mod}+N`,
      section: 'Page',
      keywords: ['new page'],
      handler: () => eventBus.emit('toolbar:add-page')
    },
    {
      id: 'reorder-pages',
      label: 'Reorder Pages',
      shortcut: 'Alt+Arrow',
      section: 'Page',
      keywords: ['move pages', 'tabs'],
      // BC-9: opens the page-actions menu (Move left/right, and the rest) on
      // whichever surface is showing — it used to focus a tab in a strip that
      // is hidden on phones and when collapsed.
      handler: () => eventBus.emit('cmd:page-actions'),
      isEnabled: hasActivePage
    },
    {
      id: 'rename-page',
      label: 'Rename Current Page',
      shortcut: 'F2',
      section: 'Page',
      keywords: ['name', 'title'],
      handler: () => eventBus.emit('cmd:rename-current-page'),
      isEnabled: hasActivePage
    },
    {
      id: 'duplicate-page',
      label: 'Duplicate Current Page',
      shortcut: mdash,
      section: 'Page',
      keywords: ['copy', 'clone'],
      handler: () => eventBus.emit('cmd:duplicate-current-page'),
      isEnabled: hasActivePage
    },
    {
      id: 'delete-page',
      label: 'Delete Current Page',
      shortcut: mdash,
      section: 'Page',
      keywords: ['remove'],
      handler: () => eventBus.emit('cmd:delete-current-page'),
      isEnabled: canDeleteActivePage
    },
    {
      id: 'toggle-preview',
      label: 'Toggle Preview',
      shortcut: mdash,
      section: 'View',
      keywords: ['preview mode'],
      handler: () => eventBus.emit('toolbar:toggle-preview')
    },
    {
      id: 'open-design',
      label: PROJECT_LABELS.design,
      shortcut: '',
      section: 'View',
      keywords: ['theme', 'design', 'colors', 'fonts'],
      handler: () => eventBus.emit('props:request-tab', { tab: 'theme' })
    },
    {
      id: 'toggle-sidebar',
      label: 'Toggle Sidebar',
      shortcut: mdash,
      section: 'View',
      keywords: ['panel', 'sections'],
      handler: () => eventBus.emit('sidebar:toggle')
    },
    {
      id: 'open-comments',
      label: PUBLISH_LABELS.comments,
      shortcut: mdash,
      section: 'Comments',
      keywords: ['review', 'feedback', 'annotations'],
      handler: () => eventBus.emit('toolbar:open-comments')
    },
    {
      id: 'next-comment',
      label: 'Next Comment',
      shortcut: 'J / ]',
      section: 'Comments',
      keywords: ['review', 'feedback', 'annotations'],
      handler: () => stepCommentSelection(1)
    },
    {
      id: 'previous-comment',
      label: 'Previous Comment',
      shortcut: 'K / [',
      section: 'Comments',
      keywords: ['review', 'feedback', 'annotations'],
      handler: () => stepCommentSelection(-1)
    },
    {
      id: 'viewport-desktop',
      label: 'Set Viewport: Desktop',
      shortcut: mdash,
      section: 'View',
      keywords: ['canvas', 'screen'],
      handler: () => eventBus.emit('toolbar:set-viewport', { name: 'desktop' })
    },
    {
      id: 'viewport-tablet',
      label: 'Set Viewport: Tablet',
      shortcut: mdash,
      section: 'View',
      keywords: ['canvas', 'screen'],
      handler: () => eventBus.emit('toolbar:set-viewport', { name: 'tablet' })
    },
    {
      id: 'viewport-mobile',
      label: 'Set Viewport: Mobile',
      shortcut: mdash,
      section: 'View',
      keywords: ['canvas', 'screen'],
      handler: () => eventBus.emit('toolbar:set-viewport', { name: 'mobile' })
    },
    {
      id: 'zoom-reset',
      label: 'Zoom 100%',
      shortcut: `${mod}+0`,
      section: 'View',
      keywords: ['canvas'],
      handler: () => eventBus.emit('toolbar:zoom-reset')
    },
    {
      id: 'zoom-in',
      label: 'Zoom In',
      shortcut: `${mod}+=`,
      section: 'View',
      keywords: ['canvas'],
      handler: () => eventBus.emit('toolbar:zoom-in')
    },
    {
      id: 'zoom-out',
      label: 'Zoom Out',
      shortcut: `${mod}+-`,
      section: 'View',
      keywords: ['canvas'],
      handler: () => eventBus.emit('toolbar:zoom-out')
    },
    {
      id: 'quick-save',
      label: SAVE_LABELS.json,
      shortcut: `${mod}+S`,
      section: 'File',
      keywords: ['save', 'quick'],
      handler: () => eventBus.emit('toolbar:quick-save')
    },
    {
      id: 'full-save',
      label: SAVE_LABELS.zip,
      shortcut: mdash,
      section: 'File',
      keywords: ['save', 'full', 'zip', 'backup'],
      handler: () => eventBus.emit('toolbar:full-save')
    },
    {
      id: 'cloud-save',
      label: SAVE_LABELS.cloud,
      shortcut: mdash,
      section: 'File',
      keywords: ['save', 'cloud', 'sync'],
      handler: () => eventBus.emit('toolbar:cloud-save')
    },
    {
      id: 'load-project',
      label: PROJECT_LABELS.openFile,
      shortcut: mdash,
      section: 'File',
      keywords: ['open', 'import', 'load'],
      handler: () => document.querySelector('#pb-load-input')?.click()
    },
    {
      id: 'open-projects',
      label: PROJECT_LABELS.projects,
      shortcut: mdash,
      section: 'File',
      keywords: ['cloud', 'list', 'projects'],
      handler: () => eventBus.emit('toolbar:open-projects')
    },
    {
      id: 'open-history',
      label: SAVE_LABELS.versions,
      shortcut: mdash,
      section: 'File',
      keywords: ['revisions', 'history', 'cloud', 'version history'],
      // BC-7: the ONE History surface, opened on its cloud scope. The docked
      // canvas view is one click from there ("Open the full canvas view").
      handler: () => eventBus.emit('toolbar:open-history-panel', { scope: 'cloud' })
    },
    {
      id: 'open-fleet',
      label: PROJECT_LABELS.fleet,
      shortcut: mdash,
      section: 'File',
      keywords: ['fleet', 'clients', 'exports', 'drift', 'outdated'],
      handler: () => eventBus.emit('toolbar:open-fleet')
    },
    {
      id: 'save-preview',
      label: PUBLISH_LABELS.preview,
      shortcut: mdash,
      section: 'File',
      keywords: ['share', 'public', 'preview'],
      handler: () => eventBus.emit('toolbar:save-preview')
    },
    {
      id: 'my-previews',
      label: PROJECT_LABELS.previews,
      shortcut: mdash,
      section: 'File',
      keywords: ['shared', 'public', 'previews'],
      handler: () => eventBus.emit('toolbar:my-previews')
    },
    {
      id: 'save-template',
      label: SAVE_LABELS.template,
      shortcut: mdash,
      section: 'File',
      keywords: ['template', 'share'],
      handler: () => eventBus.emit('toolbar:save-template')
    },
    {
      id: 'export',
      label: PUBLISH_LABELS.html,
      shortcut: mdash,
      section: 'File',
      keywords: ['download', 'zip'],
      handler: () => eventBus.emit('toolbar:export')
    },
    {
      id: 'export-11ty',
      label: PUBLISH_LABELS.eleventy,
      shortcut: mdash,
      section: 'File',
      keywords: ['download', 'zip', 'eleventy', 'cloudflare'],
      handler: () => eventBus.emit('toolbar:export-11ty')
    },
    {
      id: 'export-astro',
      label: PUBLISH_LABELS.astro,
      shortcut: mdash,
      section: 'File',
      keywords: ['download', 'zip', 'astro', 'cloudflare'],
      handler: () => eventBus.emit('toolbar:export-astro')
    },
    {
      id: 'publish-site',
      label: PUBLISH_LABELS.site,
      shortcut: mdash,
      section: 'File',
      keywords: ['publish', 'live', 'deploy', 'domain', 'url', 'host', 'pagestitch.site'],
      handler: () => eventBus.emit('toolbar:publish-site')
    },
    {
      id: 'skin-from-url',
      label: PROJECT_LABELS.importBrand,
      shortcut: mdash,
      section: 'Template',
      keywords: ['mockup', 'compose', 'client', 'website', 'quick', 'skin'],
      handler: () => eventBus.emit('toolbar:skin-from-url')
    },
    {
      id: 'start-over',
      label: PROJECT_LABELS.startOver,
      shortcut: mdash,
      section: 'File',
      keywords: ['reset', 'new project', 'clear'],
      handler: () => eventBus.emit('toolbar:start-over')
    },
    {
      id: 'open-tour',
      label: HELP_LABELS.tour,
      shortcut: mdash,
      section: 'Help',
      keywords: ['onboarding', 'walkthrough', 'tutorial', 'guide', 'tour', 'learn', 'help'],
      handler: () => eventBus.emit('cmd:open-tour')
    },
    {
      id: 'restart-tour',
      label: HELP_LABELS.restartTour,
      shortcut: mdash,
      section: 'Help',
      keywords: ['onboarding', 'walkthrough', 'tutorial', 'guide', 'reset', 'restart'],
      handler: () => eventBus.emit('cmd:restart-tour')
    },
    {
      id: 'open-templates',
      label: PROJECT_LABELS.templates,
      shortcut: mdash,
      section: 'Template',
      keywords: ['browse', 'load'],
      handler: () => eventBus.emit('toolbar:open-templates')
    },
    {
      id: 'code-injection',
      label: PROJECT_LABELS.code,
      shortcut: mdash,
      section: 'Tools',
      keywords: ['html', 'script', 'head'],
      handler: () => eventBus.emit('toolbar:open-code-injection')
    },
    {
      id: 'link-manager',
      label: PROJECT_LABELS.links,
      shortcut: mdash,
      section: 'Tools',
      keywords: ['links', 'url', 'href'],
      handler: () => eventBus.emit('toolbar:open-link-manager')
    },
    {
      id: 'history-panel',
      label: SAVE_LABELS.history,
      shortcut: mdash,
      section: 'Tools',
      keywords: ['filmstrip', 'timeline', 'restore', 'undo', 'edits'],
      handler: () => eventBus.emit('toolbar:open-history-panel')
    },
    {
      id: 'save-checkpoint',
      label: SAVE_LABELS.checkpoint,
      shortcut: mdash,
      section: 'Tools',
      keywords: ['snapshot', 'named', 'restore', 'bookmark', 'history', 'checkpoint'],
      handler: () => eventBus.emit('toolbar:open-history-panel', { focusCheckpoint: true })
    },
    {
      id: 'revision-history',
      // BC-7: this opened a second history surface ("Revision history") for
      // the records the timeline already restores. It is named for what it is
      // now — the storage manager, where old saved points are deleted.
      label: SAVE_LABELS.manageSaved,
      shortcut: mdash,
      section: 'Tools',
      keywords: ['revision history', 'snapshots', 'storage', 'delete', 'versions'],
      handler: () => eventBus.emit('toolbar:open-revision-history')
    },
    {
      id: 'keyboard-shortcuts',
      label: HELP_LABELS.shortcuts,
      shortcut: '?',
      section: 'Help',
      keywords: ['keys', 'hotkeys'],
      handler: () => eventBus.emit('toolbar:show-shortcuts')
    },
    {
      id: 'report-issue',
      label: HELP_LABELS.report,
      shortcut: mdash,
      section: 'Help',
      keywords: ['bug', 'broken', 'crash', 'problem', 'feedback', 'diagnostic', 'report'],
      handler: () => eventBus.emit('toolbar:report-issue')
    },
    {
      id: 'your-reports',
      label: HELP_LABELS.reports,
      shortcut: mdash,
      section: 'Help',
      keywords: ['issue', 'bug', 'sent', 'withdraw', 'diagnostic', 'reports'],
      handler: () => eventBus.emit('toolbar:open-issue-reports')
    }
  ];

  for (const a of actions) registerAction(a);
}

/**
 * Snapshot of every registered action (built-ins included). Read-only view
 * for tests and derived UIs (e.g. shortcuts help) — mutate via
 * registerAction/replaceActionsForType, never through this array.
 */
export function getRegisteredActions() {
  ensureBuiltins();
  return _actions.slice();
}

// ── Fuzzy search ──

/**
 * @returns {{ results: Array, total: number, truncated: boolean }}
 *   On empty query: only bounded sets (actions + pages), full list.
 *   On non-empty query: all matches, capped at MAX_RESULTS — `total` reports
 *   the pre-cap count so renderResults can show "N of M".
 */
function matchActions(query) {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    // Page entries register during shell boot, before the built-in actions
    // are lazily pushed by ensureBuiltins(). Stable-sort the bounded set so
    // built-in actions always render first regardless of registration order
    // (the cold-open palette stays "actions, then pages").
    const bounded = _actions.filter((a) => a.type === 'action' || a.type === 'page');
    bounded.sort((a, b) => {
      const rank = (t) => (t === 'action' ? 0 : 1);
      return rank(a.type) - rank(b.type);
    });
    return { results: bounded, total: bounded.length, truncated: false };
  }

  const matched = [];
  for (const action of _actions) {
    // Precomputed at register/replace time (stampHaystack); fall back to an
    // on-the-fly build only if an action slipped in without one.
    const haystack =
      action._haystack ||
      [action.label.toLowerCase(), ...(action.keywords || []).map((k) => k.toLowerCase())].join(
        ' '
      );
    if (words.every((w) => haystack.includes(w))) {
      matched.push(action);
    }
  }

  // Sort: exact prefix on label first
  const q = words.join(' ');
  matched.sort((a, b) => {
    const aPrefix = a.label.toLowerCase().startsWith(q) ? 0 : 1;
    const bPrefix = b.label.toLowerCase().startsWith(q) ? 0 : 1;
    return aPrefix - bPrefix;
  });

  const truncated = matched.length > MAX_RESULTS;
  return {
    results: truncated ? matched.slice(0, MAX_RESULTS) : matched,
    total: matched.length,
    truncated
  };
}

const TYPE_BADGE_LABEL = {
  section: 'Section',
  template: 'Template',
  page: 'Page'
};

// ── UI ──

export function openCommandPalette() {
  if (document.querySelector('.pb-cmd-overlay')) return;
  ensureBuiltins();
  eventBus.emit('command-palette:opened');

  let activeIndex = -1;
  let currentResults = [];
  let releaseActionChangeListener = null;
  const listeners = new AbortController();
  const { signal } = listeners;

  // Decision D2 and its corollary. The palette used to hang a bespoke
  // `.pb-cmd-palette` card directly off `.pb-modal-overlay`, and P11's phone
  // sheet rule is `.pb-modal-overlay .pb-modal` — so the palette was the one
  // surface in the tree the sheet could not see, and stayed a floating desktop
  // card on a phone. The census flagged it; this is the fix. `.pb-cmd-palette`
  // survives as the class carrying the bespoke internals; `.pb-modal` is now
  // the shell underneath it.
  const {
    overlay,
    body: paletteBody,
    close
  } = createModal({
    title: null,
    showClose: false,
    withFooter: false,
    maxWidth: 480,
    className: 'pb-cmd-palette',
    overlayClassName: 'pb-cmd-overlay',
    // PP-2: open-time focus goes through the kit, lazily — `input` is built
    // below, after this call returns.
    initialFocus: () => input,
    // The kit owns the trap, the teardown and the focus restore; what remains
    // genuinely local is the listener AbortController and the action-registry
    // subscription. The kit's `closed` guard makes this run exactly once.
    onClose: () => {
      listeners.abort();
      releaseActionChangeListener?.();
      releaseActionChangeListener = null;
    }
  });
  // The kit labels by a title element; this dialog has no visible title.
  overlay.setAttribute('aria-label', 'Command palette');

  const input = document.createElement('input');
  input.className = 'pb-cmd-input';
  input.type = 'text';
  input.placeholder = 'Type a command\u2026';
  input.autocomplete = 'off';
  input.setAttribute('aria-label', 'Search commands');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'true');
  input.setAttribute('aria-controls', 'pb-cmd-listbox');
  input.setAttribute('aria-activedescendant', '');

  const resultsList = document.createElement('div');
  resultsList.className = 'pb-cmd-results';
  resultsList.id = 'pb-cmd-listbox';
  resultsList.setAttribute('role', 'listbox');

  const instructions = document.createElement('span');
  instructions.id = 'pb-cmd-instructions';
  instructions.className = 'pb-sr-only';
  instructions.textContent = 'Arrow keys to navigate, Enter to select, Escape to close';
  input.setAttribute('aria-describedby', 'pb-cmd-instructions');

  paletteBody.appendChild(input);
  paletteBody.appendChild(instructions);
  paletteBody.appendChild(resultsList);

  // ── Helpers ──

  function renderResults({ results, total, truncated }) {
    currentResults = results;
    resultsList.innerHTML = '';
    activeIndex = -1;

    if (results.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'pb-cmd-empty';
      empty.textContent = 'No matching commands';
      resultsList.appendChild(empty);
      input.setAttribute('aria-activedescendant', '');
      return;
    }

    for (let i = 0; i < results.length; i++) {
      const action = results[i];
      const enabled = action.isEnabled ? action.isEnabled() : true;
      const item = document.createElement('div');
      item.className = 'pb-cmd-item';
      item.id = `pb-cmd-item-${i}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.setAttribute('aria-disabled', String(!enabled));

      const label = document.createElement('span');
      label.className = 'pb-cmd-label';
      label.textContent = action.label;

      const shortcut = document.createElement('span');
      shortcut.className = 'pb-cmd-shortcut';
      shortcut.textContent =
        action.shortcut != null && action.shortcut !== '' ? action.shortcut : '\u2014';

      const section = document.createElement('span');
      section.className = 'pb-cmd-section';
      // Non-action types get a coloured badge so users see at a glance that
      // they're inserting a section vs. running a command vs. switching pages.
      const badgeLabel = TYPE_BADGE_LABEL[action.type];
      if (badgeLabel) {
        const badge = document.createElement('span');
        badge.className = `pb-cmd-type-badge pb-cmd-type-${action.type}`;
        badge.textContent = badgeLabel;
        section.appendChild(badge);
        if (action.section) {
          section.appendChild(document.createTextNode(' '));
          section.appendChild(document.createTextNode(action.section));
        }
      } else {
        section.textContent = action.section || '';
      }

      item.appendChild(label);
      item.appendChild(shortcut);
      item.appendChild(section);

      item.addEventListener('click', () => {
        if (!enabled) return;
        close();
        action.handler();
      });

      resultsList.appendChild(item);
    }

    // Truncation footer when the result set was capped.
    if (truncated && Number.isFinite(total)) {
      const footer = document.createElement('div');
      footer.className = 'pb-cmd-footer';
      footer.setAttribute('role', 'presentation');
      footer.textContent = `Showing ${results.length} of ${total} matches \u2014 keep typing to narrow.`;
      resultsList.appendChild(footer);
    }

    // Auto-highlight first enabled item
    setActiveIndex(findNextEnabled(-1, 1));
  }

  function findNextEnabled(from, direction) {
    const len = currentResults.length;
    if (len === 0) return -1;
    let idx = from;
    for (let i = 0; i < len; i++) {
      idx = (idx + direction + len) % len;
      const action = currentResults[idx];
      if (!action.isEnabled || action.isEnabled()) return idx;
    }
    return -1;
  }

  function setActiveIndex(idx) {
    // Clear previous
    if (activeIndex >= 0) {
      const prev = resultsList.querySelector(`#pb-cmd-item-${activeIndex}`);
      if (prev) {
        prev.classList.remove('pb-cmd-item-active');
        prev.setAttribute('aria-selected', 'false');
      }
    }

    activeIndex = idx;

    if (idx >= 0) {
      const el = resultsList.querySelector(`#pb-cmd-item-${idx}`);
      if (el) {
        el.classList.add('pb-cmd-item-active');
        el.setAttribute('aria-selected', 'true');
        el.scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', el.id);
      }
    } else {
      input.setAttribute('aria-activedescendant', '');
    }
  }

  // ── Event handlers (scoped to overlay DOM lifetime) ──

  input.addEventListener(
    'input',
    () => {
      renderResults(matchActions(input.value));
    },
    { signal }
  );

  // Section/template actions can arrive after the palette is already open
  // (idle/first-open prefetch). Keep the visible result set in sync with the
  // current query instead of waiting for another keystroke.
  releaseActionChangeListener = eventBus.on(ACTIONS_CHANGED_EVENT, () => {
    if (!closed) renderResults(matchActions(input.value));
  });

  input.addEventListener(
    'keydown',
    (e) => {
      if (e.isComposing) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = findNextEnabled(activeIndex, 1);
        if (next >= 0) setActiveIndex(next);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = findNextEnabled(activeIndex, -1);
        if (prev >= 0) setActiveIndex(prev);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < currentResults.length) {
          const action = currentResults[activeIndex];
          const enabled = action.isEnabled ? action.isEnabled() : true;
          if (enabled) {
            close();
            action.handler();
          }
        }
        return;
      }
    },
    { signal }
  );

  // Backdrop dismissal, the focus trap, Escape AND open-time focus are all the
  // kit's now (initialFocus above; dismissOnBackdrop defaults true), which is
  // also what makes this a drag-dismissible sheet on a phone for free.

  // Initial render with all actions
  renderResults(matchActions(''));
}
